import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatShortDate } from '@/lib/formatDate'
import type { LoanEntryWithOutstanding } from './loanStats'

const MAX_BARS = 36

/** One bar per recorded entry (principal + interest stacked), with the outstanding after each as a line. */
export function LoanPaymentsChart({ entries }: { entries: LoanEntryWithOutstanding[] }) {
  const { format, formatCompact } = useFormatCurrency()
  const shown = entries.slice(0, MAX_BARS).reverse()
  if (shown.length === 0) return null

  const maxPay = Math.max(
    1,
    ...shown.map((e) => (e.type === 'INCREASE' ? 0 : e.principalAmount + e.interestAmount))
  )
  const outs = shown.map((e) => e.outstandingAfter)
  const outMax = Math.max(...outs)
  const outMin = Math.min(...outs)
  const outRange = outMax - outMin || 1
  // Inset from the top and bottom edges so the line never sits flat on the axis or clips.
  const lineY = (v: number) => 10 + (1 - (v - outMin) / outRange) * 80
  const points = shown
    .map((e, i) => `${((i + 0.5) / shown.length) * 100},${lineY(e.outstandingAfter)}`)
    .join(' ')

  const first = shown[0]
  const last = shown[shown.length - 1]

  return (
    <figure className="m-0 rounded-[14px] border border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 pt-3.5 pb-2">
      <div className="flex flex-wrap justify-between gap-2 text-[11.5px] text-[var(--fl-text-3)]">
        <span>Up to {formatCompact(maxPay)} per payment</span>
        <span>
          Outstanding {formatCompact(outMax)}
          {outMax !== outMin && ` → ${formatCompact(last.outstandingAfter)}`}
        </span>
      </div>
      <div
        role="img"
        aria-label={`${shown.length} entries from ${formatShortDate(first.date)} to ${formatShortDate(last.date)}. Outstanding now ${format(last.outstandingAfter)}.`}
        className="relative mt-2 h-24 md:h-40"
      >
        <div className="absolute inset-0 flex items-end gap-[3px] border-b border-[var(--fl-border)] sm:gap-1.5">
          {shown.map((e) =>
            e.type === 'INCREASE' ? (
              <div key={e.id} className="flex h-full min-w-0 flex-1 items-end">
                <div className="h-1 w-full rounded-sm bg-[var(--fl-expense)]" />
              </div>
            ) : (
              <div
                key={e.id}
                className="flex h-full min-w-0 flex-1 flex-col justify-end"
                title={`${formatShortDate(e.date)}: principal ${format(e.principalAmount)}, interest ${format(e.interestAmount)}`}
              >
                {e.interestAmount > 0 && (
                  <div
                    className="w-full rounded-t-[2px] bg-[var(--fl-warning-text)]"
                    style={{ height: `${(e.interestAmount / maxPay) * 100}%` }}
                  />
                )}
                <div
                  className={`w-full bg-[var(--fl-income)] ${e.interestAmount > 0 ? '' : 'rounded-t-[2px]'}`}
                  style={{ height: `${(e.principalAmount / maxPay) * 100}%` }}
                />
              </div>
            )
          )}
        </div>
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polyline
            points={points}
            fill="none"
            stroke="var(--fl-heading)"
            strokeWidth={2}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-[var(--fl-text-3)]">
        <span>{formatShortDate(first.date)}</span>
        <span className="font-semibold text-[var(--fl-heading)]">{formatShortDate(last.date)}</span>
      </div>
      <figcaption className="mt-1 flex flex-wrap gap-3 text-[11.5px] text-[var(--fl-text-3)]">
        <span className="flex items-center gap-1">
          <i className="inline-block h-2 w-2 rounded-sm bg-[var(--fl-income)]" />
          Principal
        </span>
        <span className="flex items-center gap-1">
          <i className="inline-block h-2 w-2 rounded-sm bg-[var(--fl-warning-text)]" />
          Interest
        </span>
        <span className="flex items-center gap-1">
          <i className="inline-block h-0.5 w-3 bg-[var(--fl-heading)]" />
          Outstanding
        </span>
        {entries.length > MAX_BARS && <span>Latest {MAX_BARS} entries</span>}
      </figcaption>
    </figure>
  )
}
